import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { StreamMessage } from '../chat/StreamMessage';
import { type Message } from '../../store/chat.store';

export interface TestStep {
  /** Skill that fired for this step. */
  skill: string;
  /** Tool the skill invoked. */
  tool: string;
  /** Record IDs the tool touched. */
  records: string;
}

interface Props {
  messages: Message[];
  steps?: TestStep[];
  onRun: (input: string) => void;
  disabled?: boolean;
}

/**
 * Dedicated conversation surface for the skill Test panel.
 *
 * Unlike the chat MessageStream this is intentionally simple: no virtualization
 * or suggested prompts, an inline Label/Run input card, and a collapsible
 * execution-trace ("steps") panel showing which skills fired and tools ran.
 */
export default function SkillTestStream({
  messages,
  steps = [],
  onRun,
  disabled,
}: Props) {
  const [input, setInput] = useState('');
  const [showSteps, setShowSteps] = useState(true);

  function run() {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onRun(trimmed);
    setInput('');
  }

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
      {/* Input card */}
      <div className="rounded-lg bg-neutral-100 p-4">
        <label
          htmlFor="skill-test-input"
          className="text-ink mb-2 block text-sm font-semibold"
        >
          Label
        </label>
        <textarea
          id="skill-test-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              run();
            }
          }}
          placeholder="Enter a message to test this skill…"
          rows={3}
          disabled={disabled}
          className="text-ink w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm placeholder-neutral-400 outline-none focus:border-neutral-500 disabled:opacity-60"
        />
        <div className="mt-3 flex justify-end">
          <button
            onClick={run}
            disabled={disabled || !input.trim()}
            className="text-ink rounded-md bg-neutral-200 px-5 py-2 text-sm font-medium hover:bg-neutral-300 active:bg-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Run
          </button>
        </div>
      </div>

      {/* Conversation */}
      {messages.map((msg) =>
        msg.role === 'user' ? (
          <div key={msg.id} className="flex justify-end">
            <div className="bg-ink/8 text-ink max-w-[85%] rounded-md px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
              <StreamMessage blocks={msg.content} status={msg.status} />
            </div>
          </div>
        ) : (
          <div key={msg.id} className="text-sm text-neutral-900">
            <StreamMessage
              blocks={msg.content}
              status={msg.status}
              toolCallLabel={msg.toolCallLabel}
            />
          </div>
        ),
      )}

      {/* Execution trace */}
      {steps.length > 0 && (
        <StepsTrace
          steps={steps}
          open={showSteps}
          onToggle={() => setShowSteps((v) => !v)}
        />
      )}
    </div>
  );
}

function StepsTrace({
  steps,
  open,
  onToggle,
}: {
  steps: TestStep[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200">
      <button
        onClick={onToggle}
        className="text-ink flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold hover:bg-neutral-50"
      >
        <ChevronDown
          size={16}
          className={['transition-transform', open ? '' : '-rotate-90'].join(
            ' ',
          )}
        />
        {open ? 'Hide steps' : 'Show steps'}
      </button>

      {open && (
        <div className="divide-code-border bg-code divide-y font-mono text-xs">
          {steps.map((step, i) => (
            <div key={i} className="flex flex-wrap gap-x-6 gap-y-1 px-4 py-2">
              <span className="text-code-bright">{step.skill}</span>
              <span className="text-green-400">{step.tool}</span>
              <span className="text-blue-400">{step.records}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
